import Button from 'components/Button';
import ContactSelector from 'components/Contact/ContactSelector';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import BACKEND_URL from 'constants/backendUrl';
import { useInvalidateContacts } from 'core/api/contacts';
import Contact from 'core/models/contact';
import Google from 'images/icons/Google.svg?react';
import Import from 'images/icons/Import.svg?react';
import Linkedin from 'images/icons/Linkedin.svg?react';
import Outlook from 'images/icons/Outlook.svg?react';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { MagnifyingGlass } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { useModalStore } from 'stores/modal';
import { useTheme } from 'styled-components';
import { POST } from 'utils/http';
import {
    SFileInputContainer,
    SFileInputTitle,
    SHelpAnchor,
    SHelpContainer,
    SHelpDescriptionContainer,
    SHelpIconContainer,
    SHelpTitle,
    SImportLoading,
    SNeedHelpContainer,
    SPasteTextarea,
} from './styles';

export type ImportedContact = {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    mobileNumber?: string;
    email?: string;
    comments?: string;
    company?: string;
    job?: string;
    civility: { Male: 'male'; Female: 'female' };
};

type Inputs = {
    files: FileList;
    csvText?: string;
};

type ImportContactsResponse = {
    importedContacts: ImportedContact[];
};

enum ImportStatus {
    CREATING = 'CREATING',
    DONE = 'DONE',
    LOADING = 'LOADING',
    READY = 'READY',
}

const importContactsRequest = async (formData: FormData): Promise<ImportContactsResponse> => {
    const response = (await POST<ImportContactsResponse>(
        `${BACKEND_URL}/api/contacts/import`,
        formData,
    )) as ImportContactsResponse;

    return response;
};

type CreateContactsResponse = {
    createdContacts: Contact[];
};

const CREATE_CHUNK_SIZE = 100;

const chunk = <T,>(items: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }

    return chunks;
};

const createContactsRequest = async (contacts: ImportedContact[]): Promise<CreateContactsResponse> => {
    const createdContacts: Contact[] = [];

    // Envoi séquentiel par lots : borne la taille de chaque requête (limite express.json)
    // ET le nombre d'INSERT parallèles déclenchés côté backend (Promise.all par lot).
    for (const batch of chunk(contacts, CREATE_CHUNK_SIZE)) {
        const response = (await POST<CreateContactsResponse>(`${BACKEND_URL}/api/contacts`, {
            contacts: batch,
        })) as CreateContactsResponse;
        createdContacts.push(...response.createdContacts);
    }

    return { createdContacts };
};

type ContactsFormProps = {
    closeModal(): void;
    pasteMode?: boolean;
};

const ContactsForm: FC<ContactsFormProps> = ({ closeModal, pasteMode = false }) => {
    const closeModalStore = useModalStore((s) => s.closeModal);
    const invalidateContacts = useInvalidateContacts();
    const theme = useTheme();
    const { t } = useTranslation();

    const [importedContacts, setImportedContacts] = useState<ImportedContact[]>([]);
    const [importStatus, setImportStatus] = useState<ImportStatus>(ImportStatus.READY);

    const helpers = [
        {
            components: {
                s: <SHelpTitle />,
            },
            i18nKey: 'contact.import.linkedin-help',
            icon: <Linkedin />,
        },
        {
            components: {
                a: (
                    <SHelpAnchor href="https://contacts.google.com" rel="noopener noreferrer" target="_blank">
                        contacts.google.com
                    </SHelpAnchor>
                ),
                s: <SHelpTitle />,
            },
            i18nKey: 'contact.import.google-help',
            icon: <Google />,
        },
        {
            components: {
                s: <SHelpTitle />,
            },
            i18nKey: 'contact.import.outlook-help',
            icon: <Outlook />,
        },
        {
            components: {
                s: <SHelpTitle />,
            },
            i18nKey: 'contact.import.import-help',
            icon: <Import />,
        },
    ];

    const stripMarkdown = (csv: string): string =>
        csv
            // [text](url) → text  (covers mailto: links on emails)
            .replace(/\[([^\]\n]+)\]\([^)\n]*\)/g, '$1')
            // **text** → text
            .replace(/\*\*([^*\n]+)\*\*/g, '$1')
            // *text* → text
            .replace(/\*([^*\n]+)\*/g, '$1')
            // `text` → text
            .replace(/`([^`\n]+)`/g, '$1');

    // form
    const methods = useForm<Inputs>();
    const { handleSubmit, register } = methods;

    const processImport = async (blob: Blob, fileName: string) => {
        try {
            const formData = new FormData();
            formData.append('file', blob, fileName);
            setImportStatus(ImportStatus.LOADING);
            const response = await importContactsRequest(formData);
            await new Promise((resolve) => setTimeout(resolve, 500));
            setImportedContacts(response.importedContacts);
            setImportStatus(ImportStatus.DONE);
        } catch {
            toast.error(t('contact.import.error'));
            setImportStatus(ImportStatus.READY);
        }
    };

    const onSubmitImport = handleSubmit(async (values: Inputs) => {
        const file = values.files?.[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const { target } = event;
                if (target && target.result) {
                    // Decode the file as ISO-8859-1 (to handle non-UTF-8 exports from Excel)
                    const decodedText = new TextDecoder('iso-8859-1').decode(target.result as ArrayBuffer);
                    const utf8Blob = new Blob([decodedText], { type: 'text/csv;charset=utf-8;' });
                    await processImport(utf8Blob, file.name);
                }
            };
            reader.readAsArrayBuffer(file);
        } else if (values.csvText?.trim()) {
            const blob = new Blob([stripMarkdown(values.csvText)], { type: 'text/csv;charset=utf-8;' });
            await processImport(blob, 'contacts.csv');
        } else {
            toast.error(t('contact.import.error'));
        }
    });

    const onSubmitCreate = async (selectedContacts: ImportedContact[]) => {
        try {
            setImportStatus(ImportStatus.CREATING);
            await createContactsRequest(selectedContacts);
            await invalidateContacts();
            toast.success(t('contact.import.success', { count: selectedContacts.length }));
            closeModalStore();
        } catch {
            // Un lot a pu échouer après que d'autres aient réussi : on resynchronise quand même
            // pour que les contacts déjà créés apparaissent dans la liste.
            await invalidateContacts();
            toast.error(t('contact.import.error'));
            closeModalStore();
        }
    };

    switch (importStatus) {
        case ImportStatus.DONE:
            return (
                <ContactSelector
                    cancel={() => closeModalStore()}
                    selectableContacts={importedContacts}
                    submit={onSubmitCreate}
                />
            );
        case ImportStatus.CREATING:
            return (
                <SImportLoading>
                    <MagnifyingGlass
                        ariaLabel="MagnifyingGlass-loading"
                        color={theme.color.secondary.main}
                        height="80"
                        width="80"
                        visible
                    />
                    <Trans i18nKey="contact.import.creating" />
                </SImportLoading>
            );
        case ImportStatus.LOADING:
            return (
                <SImportLoading>
                    <MagnifyingGlass
                        ariaLabel="MagnifyingGlass-loading"
                        color={theme.color.secondary.main}
                        height="80"
                        width="80"
                        visible
                    />
                    <Trans i18nKey="contact.import.loading" />
                </SImportLoading>
            );
        default:
            return (
                <FormProvider {...methods}>
                    <form onSubmit={onSubmitImport}>
                        {pasteMode ? (
                            <>
                                <SFileInputTitle>{t('contact.import.paste-label')}</SFileInputTitle>
                                <SPasteTextarea
                                    {...register('csvText')}
                                    placeholder={t('contact.import.paste-placeholder')}
                                    rows={8}
                                />
                                <SFileInputTitle>{t('contact.import.title')}</SFileInputTitle>
                                <SFileInputContainer>
                                    <input {...register('files')} accept=".csv" type="file" />
                                </SFileInputContainer>
                            </>
                        ) : (
                            <>
                                <SFileInputTitle>{t('contact.import.title')}</SFileInputTitle>
                                <SFileInputContainer>
                                    <input
                                        {...register('files', {
                                            required: t('common.required-field') as string,
                                        })}
                                        accept=".csv"
                                        type="file"
                                    />
                                </SFileInputContainer>
                                <SFileInputTitle>{t('contact.import.need-help')}</SFileInputTitle>
                                <SNeedHelpContainer>
                                    {helpers.map((helper) => (
                                        <SHelpContainer key={helper.i18nKey}>
                                            <SHelpIconContainer>{helper.icon}</SHelpIconContainer>
                                            <SHelpDescriptionContainer>
                                                <Trans components={helper?.components as any} i18nKey={helper.i18nKey} />
                                            </SHelpDescriptionContainer>
                                        </SHelpContainer>
                                    ))}
                                </SNeedHelpContainer>
                            </>
                        )}
                        <BaseModal.BottomActions>
                            <Button onClick={() => closeModal()} variant="text">
                                {t('common.cancel')}
                            </Button>
                            <Button submit>{t('contact.import.label')}</Button>
                        </BaseModal.BottomActions>
                    </form>
                </FormProvider>
            );
    }
};

export default ContactsForm;
